















import html2canvas from "html2canvas";
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { mapValues, forEach, set } from "lodash";

import { Http } from "@/api";
import { handlebars } from "@/utils";

// Mixin
import {
  WidgetMixin,
  EventMixin,
  ValidatorMixin,
  FieldMixin,
  Event,
  Validator,
  LayoutMixin,
} from "./mixins";

@Component({
  components: {
    WidgetGroup: () => import("@/components/WidgetGroup.vue"),
  },
})
export default class WForm extends Mixins(
  WidgetMixin,
  EventMixin,
  ValidatorMixin,
  LayoutMixin
) {
  @Getter("context/getContext")
  context!: any;

  fields: Record<string, FieldMixin> = {};
  disabled: boolean = false;

  @Prop({
    type: String,
    default: "GET",
    validator: (method: string) => ["GET", "POST"].includes(method),
  })
  method!: string;

  @Prop({ type: String })
  resource?: string;

  @Prop({ type: String })
  url?: string;

  @Prop({ type: Object })
  params?: Record<string, any>;

  @Prop({ type: Object })
  data?: string;

  @Prop({ type: String })
  screenshotPath?: string;

  get computedResource() {
    return handlebars(this.resource || "", this.context);
  }

  get computedParams(): Record<string, any> {
    const params = this.params || {};
    return mapValues(params, this.getVariable);
  }

  get computedData(): Record<string, any> {
    const data = {};
    forEach(this.data || {}, (value, key) =>
      set(data, key, this.getVariable(value))
    );
    return data;
  }

  @Event("submit::form")
  async submit() {
    // Do not submit if disabled
    if (this.disabled) {
      return;
    }

    // Disable form
    this.disable();

    // Validate the form
    if (this.validateForm()) {
      // take a screenshot if enabled
      if (this.screenshotPath) {
        const dataUrl = await this.takeScreenshot();
        this.setVariable({ path: this.screenshotPath, value: dataUrl });
      }

      // Broadcast submit event
      this.broadcastEvents("submit");

      await this.request();
    }

    // Enable form again
    this.enable();
  }

  async takeScreenshot() {
    const el = this.$refs["form"] as HTMLFormElement;
    const canvas = await html2canvas(el, {
      height: el.scrollHeight,
      scrollY: 0,
    });
    return canvas.toDataURL("image/png");
  }

  async request() {
    if (this.computedResource) {
      try {
        let response;
        switch (this.method) {
          case "GET":
            response = await Http.get(
              this.computedResource,
              {},
              this.computedParams
            );
            break;
          case "POST":
            response = await Http.post(
              this.computedResource,
              this.computedData,
              {},
              this.computedParams
            );
            break;
        }
        // Store results in the context
        this.setVariable({ path: `forms.${this.name}`, value: response });
        this.broadcastEvents("success");
      } catch {
        this.broadcastEvents("error");
      } finally {
        this.broadcastEvents("finally");
      }
    }
  }

  @Event("reset::form")
  reset() {
    Object.values(this.fields).forEach((f) => f.reset());
    this.broadcastEvents("reset");
  }

  @Event("validate::form")
  validateForm() {
    return this.validateFields() && this.validateNonFields();
  }

  validateFields(): boolean {
    return Object.values(this.fields)
      .map((f) => f.validate(f.fieldValue))
      .every((x) => x);
  }

  validateNonFields(): boolean {
    return this.validate(null);
  }

  @Event("disable::form")
  disable() {
    this.disabled = true;
  }

  @Event("enable::form")
  enable() {
    this.disabled = false;
  }

  @Provide("register")
  registerField(component: FieldMixin) {
    this.fields[component.id] = component;
  }

  @Provide("deregister")
  deregisterField(component: FieldMixin) {
    delete this.fields[component.id];
  }

  @Validator("any")
  anyValidator(_: unknown, options: { fields: string[] }) {
    return options.fields.map((f) => this.getVariable(f)).some(Boolean);
  }
}
